'use client';

import * as React from 'react';
import {Plus} from 'lucide-react';
import {cn} from '@purpose/ui/lib/utils';
import Image, {StaticImageData} from 'next/image';
import EmptyImage from '../../assets/static/empty.png';
import {useResponsive} from '@purpose/ui/hooks';
import {Button} from '../button';
import {Box} from '../box';
import typography from '@purpose/ui/styles/theme/typography';

interface EmptyStateProps {
  title: string;
  description?: string;
  src?: string | StaticImageData;
  className?: string;
  buttonProps?: {
    title: string;
    onClick: () => void;
    isIconLeft?: boolean;
    icon?: React.ReactNode;
    isLoading?: boolean;
    style?: React.CSSProperties;
    renderWrapper?: (children: React.ReactNode) => React.ReactNode;
    disabled?: boolean;
  };
  titleClassName?: string;
  descriptionClassName?: string;
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      title,
      description,
      src = EmptyImage,
      buttonProps,
      className,
      titleClassName,
      descriptionClassName
    },
    ref
  ) => {
    const {isTabletOrSmaller, breakpoint} = useResponsive();

    const getImageSize = () => {
      switch (breakpoint) {
        case 'xs':
          return 104;
        case 'sm':
          return 104;
        case 'md':
          return 124;
        case 'lg':
          return 140;
        case 'xl':
          return 140;
        default:
          return 124;
      }
    };

    const btn = buttonProps ? (
      <Box className="flex justify-center">
        <Button
          onClick={buttonProps.onClick}
          className={cn('mx-auto flex gap-4.5 px-4.5', {
            'flex-row-reverse': buttonProps.isIconLeft,
            'w-[calc(100% - 24px)] absolute bottom-6 max-w-[350px]':
              isTabletOrSmaller
          })}
          style={buttonProps?.style}
          isLoading={buttonProps.isLoading}
          disabled={buttonProps.disabled}
          aria-label={buttonProps.title}
        >
          {buttonProps.title}
          {buttonProps.icon || <Plus />}
        </Button>
      </Box>
    ) : null;
    return (
      <div
        ref={ref}
        className={cn(
          'relative mx-auto flex max-w-[426px] flex-col items-center justify-center gap-4 px-6 py-15',
          className
        )}
      >
        <Image
          alt={title}
          src={src}
          width={getImageSize()}
          height={getImageSize()}
          style={{margin: '0 auto'}}
        />
        <div className="mt-2.5 mb-5 flex flex-col gap-2.5">
          <p
            className={cn('text-center', titleClassName)}
            style={isTabletOrSmaller ? typography.h5 : typography.h4}
          >
            {title}
          </p>
          {description && (
            <p
              className={cn('mb-2.5 text-center', descriptionClassName)}
              style={
                isTabletOrSmaller ? typography.body2 : typography.subtitle2
              }
            >
              {description}
            </p>
          )}
        </div>
        {buttonProps?.renderWrapper?.(btn) || btn}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

export {EmptyState};
export type {EmptyStateProps};
